'use client';

import { useCallback, useContext, useEffect } from 'react';
import type {
  AnalyticsEvent,
  AnalyticsExperiment,
  AnalyticsIdentify,
  AnalyticsServiceAsyncMethod,
  IAnalytics,
} from './Analytics.types';
import { AnalyticsContext } from './AnalyticsProvider';

type IUseAnalytics = Required<
  Pick<IAnalytics, 'track' | 'identify' | 'reset' | 'trackExperiment'>
>;

/**
 *
 * @usage
 * ```tsx
 * const analytics = useAnalytics();
 *
 * // ...
 * await analytics.track({
 *   event: 'Form Completed'
 * });
 * ```
 */
export const useAnalytics = (): IUseAnalytics => {
  const context = useContext(AnalyticsContext);
  if (context === null) {
    throw new Error('useAnalytics must be used within a AnalyticsProvider');
  }
  const { services, queue, isInitialized } = context;

  const runServiceMethod = async (
    service: IAnalytics,
    task: AnalyticsServiceAsyncMethod
  ) => {
    try {
      await task(service);
    } catch (e) {
      // filter out errors related to ad blockers
      if (
        e instanceof Error &&
        (/.*service is not present/i.test(e.message) ||
          /.*service timed out/i.test(e.message) ||
          /operation timed out/i.test(e.message))
      ) {
        console.warn('Analytics event processing failed...', e.message);
        return;
      }
      // re-throw other errors
      throw e;
    }
  };

  const processQueueItems = useCallback(async () => {
    const tasks = [...queue.queue()];
    queue.clear();
    return await Promise.allSettled(
      tasks.map((task) =>
        services.map((service) => runServiceMethod(service, task))
      )
    );
  }, [services, queue]);

  const processQueue = useCallback(async () => {
    if (isInitialized && queue.size() > 0) {
      return processQueueItems();
    }
  }, [isInitialized, processQueueItems, queue]);

  useEffect(() => {
    processQueue();
  }, [services, isInitialized, processQueue]);

  const track = async (event: AnalyticsEvent) => {
    queue.add((service: IAnalytics) => service.track(event));
    await processQueue();
  };

  const identify = async (ai: AnalyticsIdentify) => {
    queue.add((service: IAnalytics) => service.identify?.(ai));
    await processQueue();
  };

  const reset = () => {
    services.map((service) => service.reset?.());
  };

  const trackExperiment = async (exp: AnalyticsExperiment) => {
    queue.add((service: IAnalytics) => service.trackExperiment?.(exp));
    await processQueue();
  };

  return {
    track,
    identify,
    reset,
    trackExperiment,
  };
};
